import { useState, useEffect } from 'react'
import { Radio, RadioGroup, Select, Input, Drawer, DatePicker, TimePicker, Switch, message } from '@/components/ui'
import dayjs from 'dayjs'

const { RangePicker: DateRangePicker } = DatePicker
const { RangePicker: TimeRangePicker } = TimePicker

interface ScanPoint {
  id: number
  name: string
  kind: string
  start_at: string | null
  end_at: string | null
  allow_anytime_scans: boolean
  allow_multiple_scans: boolean
  agenda_session_id: number | null
}

interface AgendaSession {
  value: number
  label: string
  starts_at: string
  ends_at: string
}

interface ScanPointDrawerProps {
  visible: boolean
  onClose: () => void
  onSubmit: (values: any) => void
  editingScanPoint: ScanPoint | null
  agendaSessions: AgendaSession[]
  eventStartDate: string
  eventEndDate: string
}

export default function ScanPointDrawer({
  visible,
  onClose,
  onSubmit,
  editingScanPoint,
  agendaSessions,
  eventStartDate,
  eventEndDate
}: ScanPointDrawerProps) {
  const [scanType, setScanType] = useState<'custom' | 'session_link' | 'event_check_in'>('custom')
  const [name, setName] = useState('')
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([null, null])
  const [timeRange, setTimeRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([null, null])
  const [allowAnytimeScans, setAllowAnytimeScans] = useState(false)
  const [checkInType, setCheckInType] = useState<'single' | 'multiple'>('single')
  const [selectedSession, setSelectedSession] = useState<number | null>(null)

  useEffect(() => {
    if (visible) {
      if (editingScanPoint) {
        // Populate form with editing data
        setScanType(editingScanPoint.kind as 'custom' | 'session_link' | 'event_check_in')
        setName(editingScanPoint.name)
        setCheckInType(editingScanPoint.allow_multiple_scans ? 'multiple' : 'single')
        setSelectedSession(editingScanPoint.agenda_session_id)
        setAllowAnytimeScans(editingScanPoint.allow_anytime_scans)

        if (editingScanPoint.start_at && editingScanPoint.end_at) {
          setDateRange([dayjs(editingScanPoint.start_at), dayjs(editingScanPoint.end_at)])
          setTimeRange([dayjs(editingScanPoint.start_at), dayjs(editingScanPoint.end_at)])
        }
      } else {
        // Reset form for new scan point
        resetForm()
      }
    }
  }, [visible, editingScanPoint])

  const resetForm = () => {
    setScanType('custom')
    setName('')
    setDateRange([null, null])
    setTimeRange([null, null])
    setAllowAnytimeScans(false)
    setCheckInType('single')
    setSelectedSession(null)
  }

  const handleSubmit = () => {
    const kind = editingScanPoint ? editingScanPoint.kind : scanType
    const values: any = {
      kind,
      allow_anytime_scans: allowAnytimeScans,
      allow_multiple_scans: checkInType === 'multiple'
    }

    // Legacy event_check_in points are edited like custom ones.
    const isCustomType = kind === 'custom' || kind === 'event_check_in'

    if (isCustomType) {
      values.name = name || editingScanPoint?.name

      if (!dateRange[0] || !dateRange[1] || !timeRange[0] || !timeRange[1]) {
        message.error('Start date, end date, start time, and end time are required for custom scan points')
        return
      }

      const startDate = dateRange[0]
      const endDate = dateRange[1]
      const startTime = timeRange[0]
      const endTime = timeRange[1]

      values.start_at = startDate
        .hour(startTime.hour())
        .minute(startTime.minute())
        .second(0)
        .toISOString()

      values.end_at = endDate
        .hour(endTime.hour())
        .minute(endTime.minute())
        .second(0)
        .toISOString()
    } else {
      if (!selectedSession) {
        message.error('Please select a session to link this scan point')
        return
      }

      values.agenda_session_id = selectedSession
    }

    onSubmit(values)
  }

  const isCustomType = scanType === 'custom' || scanType === 'event_check_in'

  return (
    <Drawer
      title={editingScanPoint ? 'Edit Scan Point' : 'Create Scan Point'}
      open={visible}
      onClose={onClose}
      onSubmit={handleSubmit}
      submitText={editingScanPoint ? 'Save Changes' : 'Create'}
    >
      {!editingScanPoint && (
        <div className="mb-6">
          <label className="block text-sm font-medium mb-2">
            How would you like to create this Scan Point?
          </label>
          <RadioGroup value={scanType} onChange={(e) => setScanType(e.target.value)} size="small">
            <Radio value="custom" size="small">Custom scan point</Radio>
            <Radio value="session_link" size="small">Link to a session</Radio>
          </RadioGroup>
        </div>
      )}

      {isCustomType ? (
        <div className="space-y-4">
          <h3 className="text-base font-semibold">Custom Scan Point</h3>

          <div>
            <label className="block text-sm font-medium mb-1">Scan Point Name</label>
            <Input
              placeholder="Enter scan point name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              size="small"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Date</label>
            <DateRangePicker
              size="small"
              value={dateRange}
              onChange={(dates) => setDateRange(dates as [dayjs.Dayjs | null, dayjs.Dayjs | null])}
              placeholder={['Start date', 'End date']}
              className="w-full"
              // minDate={dayjs(eventStartDate)}
              // maxDate={dayjs(eventEndDate)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Time</label>
            <TimeRangePicker
              size="small"
              value={timeRange}
              onChange={(times) => setTimeRange(times as [dayjs.Dayjs | null, dayjs.Dayjs | null])}
              placeholder={['Start time', 'End time']}
              className="w-full"
              format="HH:mm"
            />
          </div>

          <div className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
            <div>
              <div className="text-sm font-medium text-slate-900">Allow scans anytime</div>
              <div className="text-xs text-slate-500">Ignore the start/end time window when enabled.</div>
            </div>
            <Switch checked={allowAnytimeScans} onChange={setAllowAnytimeScans} />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              How many times can each person check in at this Scan Point?
            </label>
            <RadioGroup value={checkInType} onChange={(e) => setCheckInType(e.target.value)} size="small">
              <Radio value="single" size="small">Single Check-In</Radio>
              <Radio value="multiple" size="small">Multiple Check-In</Radio>
            </RadioGroup>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <h3 className="text-base font-semibold">Link to a session</h3>

          <div>
            <label className="block text-sm font-medium mb-1">Session</label>
            <Select
              size="small"
              value={selectedSession}
              onChange={setSelectedSession}
              placeholder="Select session"
              className="w-full"
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={agendaSessions}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              How many times can each person check in at this Scan Point?
            </label>
            <RadioGroup value={checkInType} onChange={(e) => setCheckInType(e.target.value)} size="small">
              <Radio value="single" size="small">Single Check-In</Radio>
              <Radio value="multiple" size="small">Multiple Check-In</Radio>
            </RadioGroup>
          </div>

          <div className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
            <div>
              <div className="text-sm font-medium text-slate-900">Allow scans anytime</div>
              <div className="text-xs text-slate-500">Ignore the session time window when enabled.</div>
            </div>
            <Switch checked={allowAnytimeScans} onChange={setAllowAnytimeScans} />
          </div>
        </div>
      )}
    </Drawer>
  )
}
